import { useRef, useState } from "react";
import { toast } from "sonner";
import { uploadLogoFile } from "@/hooks/use-font-families";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

interface Props {
  brandId: string;
  currentUrl: string | null;
  disabled?: boolean;
  onChange: (assetId: string | null, url: string | null) => void;
}

const ACCEPT = "image/svg+xml,image/png,image/jpeg,image/webp";
const MAX_BYTES = 5 * 1024 * 1024;

export function LogoUploader({ brandId, currentUrl, disabled, onChange }: Props) {
  const { t } = useTranslation("settings");
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function handleFile(f: File) {
    if (f.size > MAX_BYTES) {
      toast.error(t("logo.tooLarge"));
      return;
    }
    setBusy(true);
    try {
      const asset = await uploadLogoFile(brandId, f);
      onChange(asset.id, asset.url);
      toast.success(t("logo.updated"));
    } catch (e: any) {
      toast.error(e?.message ?? t("logo.uploadFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{t("logo.primaryLogo")}</h3>
        {currentUrl && !disabled && (
          <Button variant="ghost" size="sm" onClick={() => onChange(null, null)}>{t("logo.remove")}</Button>
        )}
      </div>
      <div
        className="relative h-40 rounded-md border-2 border-dashed flex items-center justify-center bg-[length:16px_16px] bg-[linear-gradient(45deg,#0001_25%,transparent_25%),linear-gradient(-45deg,#0001_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#0001_75%),linear-gradient(-45deg,transparent_75%,#0001_75%)] bg-[position:0_0,0_8px,8px_-8px,-8px_0px]"
      >
        {currentUrl ? (
          <img src={currentUrl} alt={t("logo.altText")} className="max-h-32 max-w-[80%] object-contain" />
        ) : (
          <span className="text-sm text-muted-foreground">{t("logo.noLogo")}</span>
        )}
        {busy && <div className="absolute inset-0 bg-background/60 flex items-center justify-center text-sm">{t("logo.uploading")}</div>}
      </div>
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        accept={ACCEPT}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.currentTarget.value = "";
        }}
      />
      <Button disabled={disabled || busy} onClick={() => fileRef.current?.click()}>
        {currentUrl ? t("logo.replace") : t("logo.upload")}
      </Button>
    </div>
  );
}
